import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, CaretRight } from "@phosphor-icons/react";

const LOGO_IMG =
  "/assets/hero-logo.png";

/**
 * LegalPage - shared themed layout for Shipping, Returns, Terms, Privacy.
 * Props:
 *   eyebrow: small caps label (e.g., "Policy · House Rules")
 *   title: main heading (plain string, italic word split with `|`, e.g., "Shipping|Info.")
 *   updated: "Last updated" string
 *   summary: short intro paragraph
 *   sections: [{ id, heading, body: string|Array<string> }]
 *   related: [{ label, to }]
 */
export default function LegalPage({ eyebrow, title, updated, summary, sections, related }) {
  const [main, accent] = String(title).split("|");
  return (
    <div className="min-h-screen bg-[#080412] text-white font-sans" data-testid="legal-page">
      {/* Ambient glow */}
      <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 opacity-70" style={{
        background: "radial-gradient(80% 45% at 15% 0%, rgba(123,44,255,0.18), transparent 60%), radial-gradient(70% 45% at 85% 30%, rgba(255,44,140,0.12), transparent 65%)",
      }} />

      <header className="border-b border-white/8 bg-[#080412]/85 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 md:h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="legal-brand-logo">
            <img src={LOGO_IMG} alt="House Rules" className="h-12 md:h-14 w-auto rounded-lg drop-shadow-[0_0_20px_rgba(167,66,255,0.5)]" />
          </Link>
          <Link
            to="/"
            className="font-sora text-[10.5px] font-semibold uppercase tracking-[0.24em] flex items-center gap-2 text-white/80 hover:text-brandpink transition-colors"
            data-testid="legal-back-link"
          >
            <ArrowLeft size={14} weight="bold" /> Back to Site
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-8 py-12 md:py-20">
        <div className="font-sora text-[10px] font-semibold uppercase tracking-[0.32em] text-brandpink mb-3">{eyebrow}</div>
        <h1
          className="font-display uppercase text-5xl sm:text-6xl md:text-7xl leading-[0.9] tracking-[-0.01em] mb-4 break-words"
          data-testid="legal-headline"
        >
          {main}
          {accent && (
            <>
              {" "}
              <br className="hidden sm:inline" />
              <span className="italic font-editorial text-brandpink hr-text-glow-pink">{accent}</span>
            </>
          )}
        </h1>
        {updated && (
          <div className="font-mono text-[10.5px] uppercase tracking-[0.28em] text-white/50 mb-6">
            Last updated · {updated}
          </div>
        )}

        {summary && (
          <p className="text-base md:text-lg text-white/75 leading-relaxed max-w-3xl mb-10">
            {summary}
          </p>
        )}

        {sections?.length > 1 && (
          <nav aria-label="On this page" className="rounded-2xl hr-glass p-5 md:p-6 mb-10">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck size={16} weight="duotone" className="text-brandpink" />
              <div className="font-sora font-semibold text-[10.5px] uppercase tracking-[0.28em] text-white/70">On this page</div>
            </div>
            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
              {sections.map((s, i) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    className="group flex items-center gap-2 font-sans text-sm text-white/75 hover:text-brandpink transition-colors"
                    data-testid={`legal-toc-${i}`}
                  >
                    <CaretRight size={12} weight="bold" className="text-brandpink/70 group-hover:translate-x-0.5 transition-transform" />
                    {s.heading}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        )}

        <div className="space-y-10">
          {sections?.map((s, i) => (
            <section
              key={s.id}
              id={s.id}
              className="rounded-2xl hr-glass p-6 md:p-8 scroll-mt-24"
              data-testid={`legal-section-${i}`}
            >
              <h2 className="font-display uppercase text-2xl md:text-3xl leading-tight tracking-tight mb-4 text-white">
                {s.heading}
              </h2>
              {(Array.isArray(s.body) ? s.body : [s.body]).map((para, j) =>
                typeof para === "string" ? (
                  <p key={j} className="font-sans text-[15px] leading-relaxed text-white/75 mb-3 last:mb-0">
                    {para}
                  </p>
                ) : Array.isArray(para) ? (
                  <ul key={j} className="list-none space-y-2 mb-3 last:mb-0">
                    {para.map((li, k) => (
                      <li key={k} className="flex items-start gap-2.5 font-sans text-[15px] leading-relaxed text-white/80">
                        <span className="mt-2 h-1.5 w-1.5 rounded-full bg-brandpink shrink-0" />
                        <span>{li}</span>
                      </li>
                    ))}
                  </ul>
                ) : null
              )}
            </section>
          ))}
        </div>

        {related?.length > 0 && (
          <div className="mt-14 pt-8 border-t border-white/10">
            <div className="font-sora font-semibold text-[10.5px] uppercase tracking-[0.28em] text-brandpink mb-5">Related</div>
            <div className="flex flex-wrap gap-3">
              {related.map((r) => (
                <Link
                  key={r.to}
                  to={r.to}
                  className="hr-outline inline-flex items-center gap-2 h-11 px-5 rounded-full font-sora font-semibold text-[10.5px] uppercase tracking-[0.22em] text-white hover:border-brandpink hover:text-brandpink transition-colors"
                >
                  {r.label} <CaretRight size={12} weight="bold" />
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="mt-14 text-center">
          <Link
            to="/"
            className="hr-cta inline-flex items-center gap-2 h-12 px-6 rounded-full text-white font-sora font-semibold text-[11px] uppercase tracking-[0.22em]"
            data-testid="legal-cta-home"
          >
            Ready to play? Grab the box <CaretRight size={12} weight="bold" />
          </Link>
        </div>
      </main>

      <footer className="border-t border-white/8 mt-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-6 flex items-center justify-between font-sora text-[10px] uppercase tracking-[0.28em] text-white/40">
          <div>© {new Date().getFullYear()} House Rules</div>
          <Link to="/" className="hover:text-brandpink">Home</Link>
        </div>
      </footer>
    </div>
  );
}
